import styled from '@emotion/styled/macro';
import {
    BaseButton,
    BaseInput,
    BaseNavLink,
    BaseSelect,
    DataTable,
    forTabletPortraitDown,
    PhoneField,
    Popover,
    PopoverItem,
    Spinner,
    Variables,
} from '@enter/core';
import { Icon } from '@mdi/react';
import { ButtonBar } from 'components';
import { Field } from 'formik';

interface IPasswordStrength {
    strength: string;
}

export const SubCategoryCol = styled.div`
    display: flex;
    flex-direction: column;
    border-right: 1px solid ${Variables.Colors.platinum};
    min-width: 18rem;
    width: 100%;

    ${forTabletPortraitDown(`
        padding: 36px 0;
        border-right: 0;
    `)}

    &:last-of-type {
        flex-grow: 1;
    }
`;

export const SubCategoryRow = styled.div`
    display: flex;
    flex-grow: 1;

    ${SubCategoryCol} &:nth-of-type(2) {
        flex-grow: 1;
    }

    ${forTabletPortraitDown(`
        flex-wrap: wrap;
        padding: 12px 24px;
    `)}
`;

export const SubCategoryItem = styled.div`
    overflow: auto;
    padding: 40px;

    ${forTabletPortraitDown(`
        padding: 0px 24px;
        flex-grow: 1;
    `)}
`;
export const SubCategoryItemRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
`;
export const SubCategoryItemCol = styled.div`
    display: flex;
    flex-direction: column;

    border-top: 1px solid ${Variables.Colors.platinum};
    padding: 32px 0;
`;

export const SubCategoryHeader = styled.div`
    padding: 40px;

    ${forTabletPortraitDown(`
        padding: 0px 24px;
    `)}
`;

export const SubCategoryH1 = styled.h1`
    font-size: 24px;
    font-weight: 600;
    line-height: 1;
    letter-spacing: 0.2px;
    color: ${Variables.Colors.n100};
`;

export const SubCategoryH2 = styled.h2`
    font-size: 16px;
    font-weight: 500;
    line-height: 1;
    letter-spacing: 0.2px;
    margin: 0;
    color: ${Variables.Colors.n70};
`;

export const SubCategoryText = styled.div`
    font-size: 16px;
    line-height: 1.5;
    letter-spacing: 0.2px;
    color: ${Variables.Colors.n70};
    margin: 32px 0;
`;
export const SubCategoryForm = styled.div`
    padding-bottom: 32px;
`;

export const SubCategoryAddButton = styled(BaseButton)`
    padding: 0 16px 0 8px;
`;
export const SubCategoryPlus = styled(Icon)`
    fill: ${Variables.Colors.white};
    margin-right: 4px;
`;

export const ProfileBar = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: none;
    align-items: center;
    padding: 0 32px 32px 32px;
`;

export const ProfileIcon = styled.div`
    height: 80px;
    width: 80px;
    border-radius: 80px;
    font-size: 40px;
    font-weight: 500;
    line-height: 80px;
    letter-spacing: 1.6px;
    text-align: center;
    color: ${Variables.Colors.white};
    background-color: ${Variables.Colors.r40};
`;

export const FormField = styled(Field)`
    padding: 0 16px;
    max-width: 420px;
    &:disabled {
        background-color: ${Variables.Colors.white};
    }
`;

export const FormPhone = styled(PhoneField)`
    padding: 0 16px;
    max-width: 420px;
    &:disabled {
        color: ${Variables.Colors.n70};
        outline: none;
        background-color: ${Variables.Colors.white};
    }
`;

export const Button = styled(BaseButton)`
    white-space: nowrap;
    &:not(:first-of-type) {
        margin-left: 24px;
    }
`;

export const RoleButton = styled(BaseButton)`
    white-space: nowrap;
`;

export const InputGroup = styled.div`
    display: flex;
    flex-direction: column;
`;

export const Label = styled.label`
    font-size: 18px;
    line-height: 1.22;
    color: ${Variables.Colors.n70};
    margin-top: 24px;
    margin-bottom: 8px;
`;

export const Select = styled(BaseSelect)``;

export const Input = styled(BaseInput)`
    padding: 0 16px;
    max-width: 420px;
    &:disabled {
        background-color: ${Variables.Colors.white};
    }
`;

export const ButtonGroup = styled(ButtonBar)`
    margin-top: 16px;
`;

export const PasswordStrength = styled.div<IPasswordStrength>`
    margin-top: 16px;
    color: ${(props: IPasswordStrength): string => {
        switch (props.strength) {
            case 'weak':
                return Variables.Colors.warning;
            case 'fair':
                return Variables.Colors.amber30;
            case 'strong':
                return Variables.Colors.successful;
            default:
                return Variables.Colors.n50;
        }
    }};
`;
export const ErrorMessage = styled.div`
    margin-top: 16px;
    color: ${Variables.Colors.warning};
`;

export const Table = styled(DataTable)`
    overflow-y: auto;
    & > section {
        min-height: 0;
    }
`;

export const TableHeader = styled.span`
    font-weight: 500;
`;

export const TableCell = styled.div`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: flex;
    flex-direction: column;
`;
export const TableInnerCell = styled(TableCell)`
    display: flex;
    flex-direction: column;
`;
export const TableIconCell = styled.div`
    display: flex;
    flex-direction: row;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0; // NB: needed for text-overflow
`;
export const TableIconCellProfile = styled.div`
    height: 32px;
    width: 32px;
    min-height: 32px;
    min-width: 32px;
    border-radius: 32px;
    font-size: 14px;
    font-weight: 500;
    line-height: 32px;
    letter-spacing: 0.6px;
    text-align: center;
    color: ${Variables.Colors.white};
    margin-right: 20px;
    background-color: ${Variables.Colors.n30};
`;
export const TableIconCellInvitation = styled(TableIconCellProfile)`
    border: 1px dashed ${Variables.Colors.n30};
    background-color: ${Variables.Colors.white};
`;

export const TableCellDivider = styled.div`
    height: 0px;
    margin: 8px 0;
    border-top: 1px solid ${Variables.Colors.platinum};
`;

export const TableCellTop = styled.div`
    font-size: 16px;
    font-weight: 500;
    letter-spacing: 0.2px;
    color: ${Variables.Colors.n70};
`;
export const TableCellBottom = styled.div`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    display: flex;
    align-items: center;
    flex-grow: 1;
    font-size: 15px;
    letter-spacing: 0.2px;
    color: ${Variables.Colors.n30};
`;
export const TableCellButton = styled(BaseButton)`
    font-size: 16px;
    height: auto;
    padding: 4px;
    border-radius: ${Variables.Rounded.large};
`;
export const TableCellSelect = styled(Popover)``;

export const TableCellOption = styled(PopoverItem)``;

export const TableExpanded = styled.div`
    padding: 18px;
`;
export const TableExpandedCol = styled.div`
    display: flex;
    flex-direction: column;
`;
export const TableExpandedRow = styled.div<{ align?: 'left' | 'right' }>`
    display: flex;
    justify-content: ${({ align }) => (align === 'right' ? 'flex-end' : 'flex-start')};
    padding: 12px 0;
`;

export const TableExpandedItem = styled.div`
    &:not(:first-of-type) {
        margin-left: 24px;
    }
`;

export const ReturnLink = styled(BaseNavLink)`
    color: ${Variables.Colors.n100};
    margin-top: 16px;
    margin-right: 12px;
    float: left;

    ${forTabletPortraitDown(`
        margin-top: 0;
    `)}

    &:hover {
        cursor: pointer;
    }
`;

export const Waiting = styled(Spinner)`
    margin-left: 2px;
    margin-right: 8px;
`;

export const ManageContainer = styled.div`
    display: flex;
    ${RoleButton} {
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
    }
`;

export const StyledIcon = styled(Icon)``;

export const InvitationSelectButton = styled(BaseButton)`
    white-space: nowrap;
    min-width: 40px;
    padding: 0;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    border-left: 1px solid ${Variables.Colors.white};
    &:not(:first-of-type) {
        margin-left: 24px;
    }

    ${StyledIcon} {
        fill: ${Variables.Colors.white};
    }
`;
